import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Select,
  InputNumber,
  notification,
  Row,
  Col,
  Checkbox,
  Typography,
  Space,
  Input,
} from 'antd';
import PropTypes from 'prop-types';
import { ShopStore, CurrentUserStore, AppStore } from 'stores';
import Timezones from 'components/Timezones.json';
import Languages from 'components/Languages.json';
import _ from 'lodash';
import { ExportOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { WorkOrderTypeOfServiceEnum } from 'stores/Classes/WorkOrderObject';
import axios from 'axios';

import { PitstopModal } from 'shared';

const { Title, Link } = Typography;
const { Option } = Select;

const LinkContainer = styled(Row)`
  margin-top: 8px;
  margin-bottom: 16px;
  color: #123fbf;
`;
const LinkTitle = styled.span`
  font-size: 14px;
  margin-right: 8px;
`;

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-content {
      padding: 0;

      .ant-modal-close {
        color: white;
      }
    }

    .ant-modal-header {
      background-color: #02103d;
      border-bottom: 1px solid #02103d;
      padding: 20px;
      .ant-modal-title {
        color: white;
      }
    }

    .ant-modal-body,
    .ant-modal-footer {
      padding: 0px 20px;
    }

    .ant-modal-footer {
      padding: 20px;
      border-top: solid 1px #efefef;
    }

    .footer-text {
      font-weight: bold;
      padding-right: 3vw;
    }
  }
`;

const StyledTitle = styled.div`
  padding-top: 8px;
  padding-bottom: 16px;
`;

const rawTypeOptions = Object.keys(WorkOrderTypeOfServiceEnum)
  .filter((type) => {
    return [
      'driver_identified',
      'breakdown',
      'preventive_maintenance',
      'inspection',
    ].includes(type);
  })
  .map((woRepairPriorityKey) => ({
    key: woRepairPriorityKey,
    value: WorkOrderTypeOfServiceEnum[woRepairPriorityKey],
  }));

const defaultOptions = Object.keys(WorkOrderTypeOfServiceEnum)
  .filter((type) =>
    [
      'driver_identified',
      'breakdown',
      'preventive_maintenance',
      'inspection',
    ].includes(type)
  )
  .map((key, index) => ({
    id: `default-${index}`,
    title: WorkOrderTypeOfServiceEnum[key],
    value: key,
  }));

function toSnakeCase(str) {
  return str.toLowerCase().replace(/\s+/g, '_');
}

const EditSettingsForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [shopId, setShopId] = useState(0);
  const [shopStores, setShopStores] = useState([]);
  const [taxType, setTaxType] = useState('absolute');
  const [taxValue, setTaxValue] = useState(0);
  const [taxOptions, setTaxOptions] = useState([]);
  const [isShowRepairTypes, setIsShowRepairTypes] = useState(false);

  const [repairTypes, setRepairTypes] = useState([]);
  const [modalRepairTypes, setModalRepairTypes] = useState([]);

  const userSettings = CurrentUserStore.user?.settings || {};
  const shopList = CurrentUserStore.user?.shop || [];

  const repairTypesModalId = 'REPAIR_TYPES_MODAL';

  useEffect(() => {
    setShopStores(
      shopList.map((item) => ({ label: item.name, value: item.id }))
    );
    const initialShopId = ShopStore.currentShop?.id || 0;
    setShopId(initialShopId);
    load(initialShopId);
  }, []);

  const load = async (shopId) => {
    setTaxType('');
    setTaxValue(0);
    setTaxOptions([]);
    try {
      const { result } = await ShopStore.getShopSettings(shopId);

      const { data: repairTipes } = await ShopStore.getShopRepairTypes(shopId);

      if (repairTipes?.length) {
        setRepairTypes(repairTipes);
      } else {
        setRepairTypes(defaultOptions);
      }

      const fleetSettings = _.get(result, '[0].settings.fleetsettings', {});
      const value = _.get(fleetSettings, 'markAsCompleteThresholdHours', 5);

      form.setFieldsValue({
        taxType: fleetSettings.taxType,
        taxValue: fleetSettings.taxValue,
        taxOptions: fleetSettings.taxOptions,
      });
      setTaxType(fleetSettings.taxType);
      setTaxValue(fleetSettings.taxValue);
      setTaxOptions(fleetSettings.taxOptions);
    } catch (e) {
      console.error('error', e);
    } finally {
      setLoadingInitial(false);
    }
  };

  const handleShopChange = (v) => {
    setShopId(v);
    load(v);
  };

  const updateFleetRule = async (values, shopId) => {
    try {
      const settings = await ShopStore.getShopSettings(shopId);
      let currentFleetSettings = _.get(
        settings.result,
        '[0].settings.fleetsettings',
        {}
      );
      currentFleetSettings = { ...currentFleetSettings, ...values };
      _.set(
        settings.result,
        '[0].settings.fleetsettings',
        currentFleetSettings
      );

      await ShopStore.upsertShopSettings(shopId, settings.result[0].settings);
      notification.success({
        message: 'Rules updated',
        key: 'fleetRulesSuccess',
        description: <p>The Fleet rules were updated!</p>,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onFinish = async (values) => {
    const { taxOptions, taxType, taxValue, ...filteredValues } = values;
    await updateFleetRule({ taxOptions, taxType, taxValue }, values.shopId);
    setLoading(true);
    const previousLanguage = CurrentUserStore.user?.settings?.preferredLanguage;
    await CurrentUserStore.user.updateSettings(filteredValues);
    setLoading(false);
    notification.success({
      message: 'Success',
      description: 'Settings Updated',
      duration: 3,
    });

    if (values.preferredLanguage !== previousLanguage) {
      setTimeout(() => window.location.reload(), 1500);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    setModalRepairTypes(repairTypes.map((type) => ({ ...type })));
    setIsShowRepairTypes(true);

    AppStore.openModals.set(repairTypesModalId, true);
  };

  const updateTitle = (index, newTitle) => {
    const newTypes = [...modalRepairTypes];
    newTypes[index].title = newTitle;
    newTypes[index].value = toSnakeCase(newTitle);
    setModalRepairTypes(newTypes);
  };

  const removeType = (index) => {
    setModalRepairTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const addNewType = () => {
    setModalRepairTypes((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, title: '', value: '' },
    ]);
  };

  const handleFinish = async () => {
    const payload = modalRepairTypes.map(({ title }) => ({
      title,
      value: toSnakeCase(title),
    }));

    try {
      const { repairTipes } = await ShopStore.saveShopRepairTypes(
        shopId,
        payload
      );
      setRepairTypes(modalRepairTypes);
      AppStore.openModals.set(repairTypesModalId, false);

      notification.success({
        message: 'Repair Type(s) updated',
        key: 'repairTypesSuccess',
        description: <p>Your Repair Type(s) has been successfully updated!</p>,
      });
    } catch (error) {
      console.log('Error: ', error);
    }
  };

  const currentTaxType = Form.useWatch('taxType', form) || 'percentage';

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        className="edit-profile-form"
        initialValues={{
          timezone: userSettings.timezone || 'America/New_York',
          preferredLanguage: userSettings.preferredLanguage || 'en',
          odometer: CurrentUserStore.user.displayedOdometer,
          fuelUnit: userSettings.fuelUnit || 'litre',
          laborRate: userSettings.laborRate || '0',
          shopId,
          taxType,
          taxValue,
          taxOptions,
        }}
      >
        <Title level={3} style={{ fontWeight: 'normal' }}>
          General
        </Title>
        <Form.Item
          label="Timezone"
          name="timezone"
          rules={[
            {
              required: true,
              message: 'Please input your preferred timezone!',
            },
          ]}
        >
          <Select showSearch placeholder="Timezone">
            {Object.keys(Timezones).map((key) => (
              <Option key={key} value={key}>
                {Timezones[key]}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Preferred Language"
          name="preferredLanguage"
          rules={[
            {
              required: true,
              message: 'Please input your preferred language!',
            },
          ]}
        >
          <Select showSearch placeholder="Preferred Language">
            {Object.keys(Languages).map((key) => (
              <Option key={key} value={key}>
                {Languages[key]}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Title level={3} style={{ fontWeight: 'normal' }}>
          Vehicles
        </Title>
        <Form.Item
          label="Odometer Unit"
          name="odometer"
          rules={[
            { required: true, message: 'Please input your preferred unit!' },
          ]}
        >
          <Select placeholder="Odometer Unit">
            <Option value="km">KM</Option>
            <Option value="miles">Miles</Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Fuel Unit"
          name="fuelUnit"
          rules={[
            { required: true, message: 'Please input your preferred unit!' },
          ]}
        >
          <Select placeholder="Fuel Unit">
            <Option value="Litre">Litre</Option>
            <Option value="Gallons">Gallons</Option>
          </Select>
        </Form.Item>
        <Title level={3} style={{ fontWeight: 'normal' }}>
          Work Orders
        </Title>
        <Form.Item label="Set Hourly Labor Rate" name="laborRate">
          <InputNumber
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/\$\s?|,(?=\d{3})/g, '')}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Select Fleet" name="shopId">
          <Select onChange={handleShopChange}>
            {shopStores.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item label="Tax type" name="taxType">
              <Select
                placeholder="Tax type"
                onChange={(e) => {
                  if (e === 'absolute') {
                    form.setFieldValue('taxOptions', []);
                  }
                }}
              >
                <Option value="percentage">%</Option>
                <Option value="absolute">$</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Tax value" name="taxValue">
              <InputNumber
                formatter={(value) =>
                  `${currentTaxType === 'absolute' ? '$ ' : ''}${value}`.replace(
                    /\B(?=(\d{3})+(?!\d))/g,
                    ','
                  )
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                style={{
                  width: '100px',
                  marginRight: '30px',
                  marginLeft: '5px',
                }}
              />
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item label="Tax options" name="taxOptions">
              <Checkbox.Group
                disabled={form.getFieldValue('taxType') === 'absolute'}
                options={['Parts', 'Miscellaneous', 'Labor']}
              />
            </Form.Item>
          </Col>
        </Row>
        {!ShopStore.currentShop.parentShopId && (
          <LinkContainer gutter={16}>
            <Col>
              <Link href="#" target="_blank" onClick={handleClick}>
                <LinkTitle>Add/Change Repair Type Options</LinkTitle>
                <ExportOutlined />
              </Link>
            </Col>
          </LinkContainer>
        )}
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Info
          </Button>
        </Form.Item>
      </Form>

      <StyledModal
        title="Edit Repair Type"
        id={repairTypesModalId}
        closable={true}
        cancelButtonProps={{ style: { display: 'none' } }}
        okButtonProps={{ style: { display: 'none' } }}
        style={{ marginTop: 80 }}
        bodyStyle={{ paddingBottom: 20 }}
        onOk={() => {}}
        width={465}
        footer={
          <>
            <Button
              color="danger"
              variant="text"
              key="cancel"
              onClick={() => {
                AppStore.openModals.set(repairTypesModalId, false);
              }}
            >
              Cancel
            </Button>
            <Button key="submit" type="primary" onClick={handleFinish}>
              Finish
            </Button>
          </>
        }
      >
        {/* Some content */}
        <StyledTitle>
          You can change the name of each repair type, as well as add and remove
          repair types.
        </StyledTitle>

        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {modalRepairTypes.map((type, index) => (
              <Row gutter={[16, 16]} align={'middle'} key={index}>
                <Col span={22}>
                  <Input
                    value={type.title}
                    onChange={(e) => updateTitle(index, e.target.value)}
                    placeholder="Enter repair type"
                  />
                </Col>
                <Col span={2}>
                  <DeleteOutlined
                    onClick={() => removeType(index)}
                    style={{ color: 'red', cursor: 'pointer', fontSize: 22 }}
                  />
                </Col>
              </Row>
            ))}
          </Space>
        </div>
        <div>
          <Button color="primary" variant="outlined" onClick={addNewType}>
            + Add New Repair Type
          </Button>
        </div>
      </StyledModal>
    </>
  );
};

export default EditSettingsForm;
